import { Link as GatsbyLink, navigate } from "gatsby";
import React, { isValidElement, cloneElement, useMemo } from "react";
import { getBasePathForLanguage } from "../../../sanity/plugins/multilanguage";
import { stripSlashes } from "../../lib/helpers";
import { useFetchDocumentByReference } from "../../pages/preview";
import { Button, ButtonProps } from "../Button/Button";

export type LinkData = {
  externalUrl?: string;
  page?: {
    language?: string;
    slug?: { current: string };
    title?: string;
    _ref?: string;
  };
  title?: string;
  style?: string;
  ariaLabel?: string;
};

export type ResolvedLinkData = {
  path?: string;
  href?: string;
  title?: string;
  disabled?: boolean;
  ariaLabel?: string;
};
export interface LinkProps {
  to: LinkData;
  children?: React.ReactNode;
  className?: string;
  classAnchorCustom?: string;
  component?: (link: ResolvedLinkData) => React.ReactNode;
}

export const Link: React.FC<LinkProps> = ({
  to,
  children,
  className,
  component,
  classAnchorCustom,
}) => {
  const classes = useMemo(() => {
    if (classAnchorCustom) {
      return classAnchorCustom;
    }
    if (!classAnchorCustom) {
      return className;
    }
  }, [classAnchorCustom, className]);

  const resolvedLink: ResolvedLinkData = useResolveLinkData(to);
  const componentNode = component && component(resolvedLink);

  const isChildrenButton = to?.style && to?.style === "button";
  const isChildrenComponentNode = checkIfComponentNodeIsButton(componentNode);

  let ariaLabel;
  if (to?.page?.language === "de" && to?.page?.title === "CASIO Education") {
    ariaLabel = "Casio Education – Zur Startseite gehen";
  }

  if (to?.page?.language === "de" && to?.page?.title === "ClassPad Plus") {
    ariaLabel = "ClassPad Plus – Info-Seite zu ClassPad Plus öffnen";
  }

  if (to?.page?.language === "de" && to?.page?.title === "Lizenzen") {
    ariaLabel = "Lizenzen – Info-Seite zu Lizenzen öffnen";
  }

  if (to?.page?.language === "de" && to?.page?.title === "Lehrersupport") {
    ariaLabel = "Lehrersupport – Info-Seite zu Lehrersupport öffnen";
  }

  if (
    to?.page?.language === "fr" &&
    to?.page?.title === "Calculatrices en ligne"
  ) {
    ariaLabel =
      "Calculatrice en ligne - Ouvrir la page des calculatrices en ligne CASIO dans un nouvel onglet du navigateur";
  }

  if (
    to?.page?.language === "fr" &&
    to?.page?.title === "POLITIQUE DE PROTECTION DES DONNEES – CASIO EDUCATION"
  ) {
    ariaLabel =
      "Politique de confidentialité - Ouvrir la politique de confidentialité dans un nouvel onglet du navigateur";
  }

  if (
    to?.page?.language === "fr" &&
    to?.page?.title === "Documentation pour le GAR"
  ) {
    ariaLabel =
      "Documentation juridique relative au GAR - Ouvrir la documentation juridique relative au GAR dans un nouvel onglet du navigateur";
  }

  if (
    to?.page?.language === "fr" &&
    to?.page?.title === "Conditions de Service"
  ) {
    ariaLabel =
      "Conditions de service - Ouvrir les conditions de service dans un nouvel onglet du navigateur";
  }

  if (resolvedLink.path) {
    if (isChildrenButton) {
      console.log("run 1");
      const ButtonChild: React.ReactElement = isValidElement(children) ? (
        cloneElement(
          children as React.ReactComponentElement<React.FC<ButtonProps>>,
          {
            onClick: () => {
              navigate(resolvedLink.path || "");
            },
            // title: children.props.label,
          }
        )
      ) : (
        <></>
      );
      return ButtonChild;
    }

    if (isChildrenComponentNode) {
      console.log("run 2");
      const ButtonComponent: React.ReactElement = isValidElement(
        componentNode
      ) ? (
        cloneElement(
          componentNode as React.ReactComponentElement<React.FC<ButtonProps>>,
          {
            onClick: () => {
              navigate(resolvedLink.path || "");
            },
            // title: componentNode.props.label,
          }
        )
      ) : (
        <></>
      );

      return ButtonComponent;
    }
    console.log("run 3");
    return (
      <GatsbyLink
        to={resolvedLink.path}
        className={classes}
        onClick={(e) => {
          e.preventDefault();
          window.open(resolvedLink.path, "_blank", "noopener,noreferrer");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            window.open(resolvedLink.path, "_blank", "noopener,noreferrer");
          }
        }}
        aria-label={resolvedLink.ariaLabel ? resolvedLink.ariaLabel : ariaLabel}
      >
        {componentNode || children || resolvedLink.title}
      </GatsbyLink>
    );
  } else {
    const isAnchor = resolvedLink.href?.startsWith("#");
    if (isChildrenButton) {
      console.log("run 4");
      const ButtonChild: React.ReactElement = isValidElement(children) ? (
        cloneElement(
          children as React.ReactComponentElement<React.FC<ButtonProps>>,
          {
            onClick: () => {
              navigate(resolvedLink.href || "");
            },
            // title: children.props.label,
          }
        )
      ) : (
        <></>
      );
      return ButtonChild;
    }
    if (isChildrenComponentNode) {
      console.log("run 5");
      const ButtonComponent: React.ReactElement = isValidElement(
        componentNode
      ) ? (
        cloneElement(
          componentNode as React.ReactComponentElement<React.FC<ButtonProps>>,
          {
            onClick: () => {
              navigate(resolvedLink.href || "");
            },
            // title: componentNode.props.label,
          }
        )
      ) : (
        <></>
      );

      return ButtonComponent;
    }
    console.log("run 6");
    return (
      <a
        href={resolvedLink.href}
        target={isAnchor ? "" : "_blank"}
        className={classes}
        rel="noreferrer"
        aria-label={resolvedLink.ariaLabel}
        tabIndex={0}
      >
        {componentNode || children || resolvedLink.title}
      </a>
    );
  }
};

function checkIfComponentNodeIsButton(node: React.ReactNode): boolean {
  if (React.isValidElement(node)) {
    if (node.type === Button) {
      return true;
    }
  }

  return false;
}

function useResolveLinkData(linkData: LinkData): ResolvedLinkData {
  // console.log('useResolveLinkData ', linkData);
  const externalLink = linkData?.externalUrl;
  const pageReference = linkData?.page;

  const documentByReference = useFetchDocumentByReference(pageReference?._ref);

  if (externalLink) {
    const title = linkData?.title || externalLink;
    return { href: externalLink, title: title, ariaLabel: linkData?.ariaLabel };
  } else if (pageReference) {
    /*
     * If this is a link in a preview, we need to fetch the referenced page again,
     * because the preview root data only comes with an id and is unfortunately
     * missing the slug...
     */
    const page = pageReference.slug ? pageReference : documentByReference;
    const slug = page?.slug?.current;
    const target = getBasePathForLanguage(page?.language) + stripSlashes(slug);
    const title = linkData?.title || page?.title || slug;

    return { path: target, title: title, ariaLabel: linkData?.ariaLabel };
  } else {
    return {
      title: linkData?.title,
      disabled: true,
      ariaLabel: linkData?.ariaLabel,
    };
  }
}
